import { useMemo, useCallback, type ReactNode } from 'react';
import type {
  PrimaryCountryISO,
  PrimaryCountryISOElseGlobal,
} from '@/constants/countries';
import { useTheme } from '@simplywallst/ui-core';
import { MinimalDropdown } from '@/components/Dropdown';
import { getFlagCharacter } from '@utilities/getFlagCharacter';
import useWindowSize from '@/hooks/useWindowSize';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

interface MarketOption {
  option: PrimaryCountryISO | string;
  href?: string;
  label: string;
}

type IndustryOption = {
  option: string;
  label: string;
};

interface HasIndustryFilterProps {
  hideIndustryFilter: false;
  industryOptions: IndustryOption[];
  selectedIndustry: string;
  onIndustryChange: (industry: string) => void;
}

interface HasNoIndustryFilterProps {
  hideIndustryFilter: true;
  industryOptions?: never;
  selectedIndustry?: never;
  onIndustryChange?: never;
}

type IndustryFilterProps = HasIndustryFilterProps | HasNoIndustryFilterProps;

type Props = {
  marketOptions: MarketOption[];
  selectedMarket: PrimaryCountryISOElseGlobal;
  onMarketChange: (market: PrimaryCountryISOElseGlobal) => void;
  filtersAddon?: ReactNode;
  children?: ReactNode;
} & IndustryFilterProps;

export const MarketIndustryFilter = ({
  marketOptions,
  industryOptions,
  selectedMarket,
  selectedIndustry,
  hideIndustryFilter,
  onMarketChange,
  onIndustryChange,
  filtersAddon,
  children,
}: Props) => {
  const theme = useTheme();
  const { width: screenWidth = 0 } = useWindowSize();
  const { isDesktop, os } = useUserAgent();
  const isLargeScreen =
    screenWidth === 0 ? isDesktop : screenWidth > theme.mdMinRaw;
  const marketLabel = useMemo(() => {
    const countryFlag =
      os === 'Windows'
        ? ''
        : getFlagCharacter(selectedMarket as PrimaryCountryISO);
    return selectedMarket === 'global'
      ? '🌎\u00A0 Global'
      : `${countryFlag}\u00A0 ${selectedMarket.toUpperCase()} ${
          isLargeScreen ? 'Market' : ''
        }`;
  }, [selectedMarket, isLargeScreen, os]);
  const industryLabel = useCallback((): string => {
    if (industryOptions && industryOptions.length > 0) {
      const currentIndustry = industryOptions.find(
        (i) => i.option === selectedIndustry
      );
      if (currentIndustry && currentIndustry.label !== 'Any') {
        return currentIndustry.label;
      }
    }
    return 'Industry';
  }, [selectedIndustry, industryOptions]);

  return (
    <div className="flex flex-row gap-2">
      <div className="flex flex-wrap gap-inherit">
        <div className="flex flex-wrap gap-inherit whitespace-nowrap">
          <MinimalDropdown
            displayType="inline-block"
            canSearch={true}
            name="basic-filter-market"
            options={marketOptions}
            onSelect={(option: string) =>
              onMarketChange(option as PrimaryCountryISOElseGlobal)
            }
            value={selectedMarket}
            autoComplete="off"
            placeholder="Search e.g. United States"
            label={marketLabel}
          />
          {!hideIndustryFilter && (
            <MinimalDropdown
              displayType="inline-block"
              canSearch={true}
              options={industryOptions}
              onSelect={onIndustryChange}
              value={selectedIndustry}
              name="basic-filter-industry"
              autoComplete="off"
              placeholder="Search e.g. Automobiles"
              label={industryLabel()}
            />
          )}
        </div>
        {filtersAddon}
      </div>
      {children}
    </div>
  );
};
